// src/components/LoginForm.js
import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useNavigate } from 'react-router-dom';
import { auth, provider, db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from "firebase/firestore";
import ProgressBar from './ProgressBar'; // Componente de loading
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import '../styles/LoginSignup.css'; // O CSS aprimorado da resposta anterior
import logo from '../assets/logo.png';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Estado para erros de validação do formulário
  const [formErrors, setFormErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(''); // Erro principal do Firebase
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para o fluxo de "Completar Perfil"
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [name, setName] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  // Função para validar o formulário de login antes do envio
  const validateLoginForm = () => {
    const errors = {};
    if (!email) {
      errors.email = "O e-mail é obrigatório.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "O formato do e-mail é inválido.";
    }
    if (!password) {
      errors.password = "A senha é obrigatória.";
    } else if (password.length < 6) {
      errors.password = "A senha deve ter no mínimo 6 caracteres.";
    }
    setFormErrors(errors);
    // Retorna true se o objeto de erros estiver vazio
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    // Limpa erros antigos antes de validar e enviar
    setError('');

    // Valida o formulário. Se não for válido, interrompe a execução.
    if (!validateLoginForm()) {
      return;
    }

    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await checkUserInfo(userCredential.user);
    } catch (err) {
      // Mapeia erros do Firebase para mensagens amigáveis
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('E-mail ou senha inválidos.');
          break;
        case 'auth/too-many-requests':
          setError('Acesso temporariamente bloqueado por muitas tentativas. Tente novamente mais tarde.');
          break;
        default:
          setError('Ocorreu um erro ao fazer login. Tente novamente.');
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    setFormErrors({});
    try {
        const result = await signInWithPopup(auth, provider);
        const user = result.user;

        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            await setDoc(userRef, {
                name: user.displayName || "", // Pega o nome do Google se disponível
                email: user.email,
                uid: user.uid,
                empresa_id: "", // Inicialmente vazio
                role: "Colaborador", // Cargo padrão
            });
            // Se o nome já veio do Google, não precisa pedir de novo
            if (user.displayName) {
              await checkUserInfo(user);
            } else {
              setShowCompleteProfile(true);
            }
        } else {
            await checkUserInfo(user);
        }
    } catch (err) {
        if (err.code !== 'auth/popup-closed-by-user') {
            setError('Falha na autenticação com o Google. Tente novamente.');
        }
    } finally {
        setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim()) {
        setError("O nome completo é obrigatório.");
        return;
    }

    setLoading(true);
    try {
        const user = auth.currentUser;
        if (user) {
            const userRef = doc(db, "users", user.uid);
            await setDoc(userRef, { name: name.trim() }, { merge: true });
            
            setSuccess("Perfil salvo com sucesso! Redirecionando...");
            setTimeout(async () => {
                await checkUserInfo(user); // Re-verifica para redirecionar corretamente
            }, 2000);
        } else {
            throw new Error("Usuário não autenticado.");
        }
    } catch (err) {
        setError("Erro ao salvar o perfil: " + err.message);
    } finally {
        setLoading(false);
    }
  };

  // Esta função centraliza a lógica de verificação de permissões e redirecionamento
  const checkUserInfo = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
        const userData = userDoc.data();

        // Validações críticas de acesso
        if (!userData.empresa_id) {
            setError('Acesso negado: Seu usuário não está associado a uma empresa.');
            auth.signOut(); // Desloga o usuário por segurança
            return;
        }
        if (!userData.role) {
            setError('Acesso negado: Seu usuário não possui um cargo definido.');
            auth.signOut(); // Desloga o usuário por segurança
            return;
        }

        // Atualiza o último login e redireciona
        await setDoc(userRef, { lastLogin: new Date() }, { merge: true });

        // Salva o timestamp de login para controle de sessão de 24h
        localStorage.setItem('sessionStart', Date.now().toString());

        // Lógica de navegação baseada no cargo (role)
        switch (userData.role) {
            case 'Master':
                navigate('/dashboard');
                break;
            case 'G1':
                navigate('/dashboard');
                break;
            case 'Lider':
                navigate('/dashboard');
                break;
            case 'Colaborador':
                navigate('/dashboard');
                break;
            default:
                navigate('/dashboard'); // Rota padrão
                break;
        }
    } else {
        // Se o documento do usuário não existe por algum motivo, solicita a criação do perfil
        setShowCompleteProfile(true);
    }
  };


  return (
    <div className="login-container">
      {!showCompleteProfile ? (
        // --------- TELA DE LOGIN ---------
        <>
          <img src={logo} alt="Logo da Empresa" className="logo" />
          <h2 className="form-title">Seus dados, seu ritmo, seu RH em evolução.</h2>
          
          <form onSubmit={handleLogin} noValidate>
            <div className="input-group">
              <input
                required
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
              />
              <label className="user-label">E-mail *</label>
              {formErrors.email && <p className="error-text">{formErrors.email}</p>}
            </div>

            <div className="input-group">
              <input
                required
                type={showPassword ? "text" : "password"}
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
              />
              <label className="user-label">Senha *</label>
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
              {formErrors.password && <p className="error-text">{formErrors.password}</p>}
            </div>

            <div className="forgot-password">
              <a onClick={() => navigate('/forgot-password')}>Esqueceu a senha?</a>
            </div>

            {/* Exibe o erro principal do Firebase aqui */}
            {error && <p className="error">{error}</p>}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? <ProgressBar /> : 'Acessar conta'}
            </button>
          </form>

          <button onClick={handleGoogleLogin} className="google-login" disabled={loading}>
            <FcGoogle size={22} style={{ marginRight: '10px' }} />
            {loading ? 'Aguarde...' : 'Login com Google'}
          </button>
        </>
      ) : (
        // --------- TELA DE COMPLETAR PERFIL ---------
        <>
          <img src={logo} alt="Logo" className="logo" />
          <h2 className="form-title">Só mais um passo!</h2>
          <p style={{textAlign: 'center', marginBottom: '1.5rem', color: '#555'}}>
            Precisamos do seu nome completo para criar seu perfil.
          </p>
          <form onSubmit={handleSaveProfile} noValidate>
            <div className="input-group">
              <input
                required
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                autoComplete="name"
              />
              <label className="user-label">Nome Completo *</label>
            </div>
            
            {error && <p className="error">{error}</p>}
            {success && <p className="success">{success}</p>}

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? <ProgressBar /> : 'Salvar e Acessar'}
            </button>
          </form>
        </>
      )}

      <div className="recaptcha-notice">
        Este site é protegido pelo reCAPTCHA e pelo Google. 
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer"> Política de Privacidade</a> e 
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer"> Termos de Serviço</a> se aplicam.
      </div>
    </div>
  );
};

export default LoginForm;